import React, {
  useCallback,
  useState,
  useRef,
  useMemo,
  useEffect,
} from "react";
import { Flex, Button, message, Spin } from "antd";
import { ShrinkOutlined, ExpandAltOutlined } from "@ant-design/icons";
import "./SearchFile.scss";
import { handleApiError } from "../../utils/CommonFunctions";
import ApiService from "../../API/apiService";
import { API_ENDPOINTS } from "../../API/endpoints";
import formatDate from "../../utils/FormatDate";
import SearchResultItem from "./SearchResultItem";
import { useBridgeStore } from "../../store/apis/useBridgeStore";
import { useNavigate } from "react-router-dom";

export default function MessageSearchSection({ isExpanded, onSectionUpdate }) {
  // Local state instead of Redux
  const navigate = useNavigate();

  const [searchedMsgData, setSearchedMsgData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const searchParticularRecord = useBridgeStore(
    (state) => state.searchParticularRecord
  );
  const searchMsgText = useBridgeStore((state) => state.searchMsgText);
  const setJumpConfig = useBridgeStore((store) => store.setJumpConfig);

  const SearchCodeRef = useRef(null);

  // Helper functions
  const checkTextGlobalOrIndividual = useCallback((input) => {
    const pattern = /^in:@[\w_-]+(.*)$/;
    const match = input?.match(pattern);

    return {
      match: !!match,
      text: match ? match[1] : input || "",
    };
  }, []);

  const getTotalMessages = useCallback((data) => {
    return data.reduce((total, item) => total + item.totalMessages, 0);
  }, []);

  const getSearchedMsg = async () => {
    let textObject = checkTextGlobalOrIndividual(searchMsgText.value);
    const reqBody = {
      search: textObject.text,
    };
    if (textObject.match) {
      reqBody[searchParticularRecord?.isChannel ? "channelId" : "userId"] =
        searchParticularRecord.id;
    }
    try {
      setIsLoading(true);
      const response = await ApiService.post(
        API_ENDPOINTS.searchMessage,
        reqBody
      );

      if (response.status == 1) {
        setSearchedMsgData(response.data);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSearchedMsg();
  }, [searchMsgText]);

  const jumpToMsg = async (data) => {
    const reqBody = {
      messageId: data._id,
      senderId: data.senderInfo._id,
    };
    if (data?.channelId) {
      reqBody.channelId = data?.channelId;
    }
    if (!data?.channelId) {
      reqBody.receiverId = data.receiverId;
    }
    try {
      const response = await ApiService.post(
        API_ENDPOINTS.jumpToMessage,
        reqBody
      );

      if (response.status == 1) {
        let sendObj = {
          pageNo: response?.data?.pageNumber,
          msgID: data._id,
          active: true,
          isChannel: data?.channelId ? true : false,
        };
        setJumpConfig(sendObj);
        const UserData = JSON.parse(localStorage.getItem("userData"));
  let rout=UserData?.domain || 'elsnertech'
        localStorage.setItem("FromJump", JSON.stringify(sendObj));

        if (data?.channelId) {
          navigate(`/${rout}/channels/${response?.data?.redirectURL}`);
        }

        if (!data?.channelId) {
          navigate(`/${rout}/message/${response?.data?.redirectURL}`);
        }
        if (window.innerWidth <= 767) {
          // dispatch(setSearchModal(false));
        }
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  // Memoized values
  const totalMessagesCount = useMemo(
    () => getTotalMessages(searchedMsgData),
    [searchedMsgData, getTotalMessages]
  );

  const displayName = "";

  const modalClasses = useMemo(
    () =>
      `sidebar-info search-result-wrapper visible ${
        isExpanded ? "expand" : ""
      }`,
    [isExpanded]
  );

  // Render functions
  const renderLoadingState = () => (
    <ul className="chat-list">
      <div className="no-results__text-container">
        <div className="no-results__title">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "60px",
              width: "100%",
            }}
            className="spin_loader"
          >
            <Spin size="large" />
          </div>
          <span>Searching...</span>
        </div>
      </div>
    </ul>
  );

  const renderEmptyState = () => (
    <ul className="chat-list">
      <div className="no-results__wrapper">
        <div className="no-results__variant-wrapper">
          <span className="no-results__icon">
            <i className="fa-solid fa-magnifying-glass"></i>
          </span>
        </div>
        <div className="no-results__text-container">
          <h3 className="no-results__title">
            <span>No results for</span>
          </h3>
          <div className="no-results__subtitle">
            <span>Check the spelling or try another search.</span>
          </div>
        </div>
      </div>
    </ul>
  );

  const renderSearchResults = () => (
    <ul className="chat-list">
      {searchedMsgData.map((details, indexParent) => (
        <React.Fragment key={`${details.date}-${indexParent}`}>
          <li className="chat-list-item horizontal-line">
            <Flex className="chat-date-info-wrapper">
              <div className="chat__date">
                <span className="date">{formatDate(details.date)}</span>
                <hr className="hr-line" />
              </div>
            </Flex>
          </li>
          {details.messages.map((record, index) => (
            <SearchResultItem
              key={record._id}
              Record={record}
              index={index}
              indexParent={indexParent}
              totalMessages={details.messages.length}
              searchedDataLength={searchedMsgData.length}
              SearchCodeRef={SearchCodeRef}
              jumpToMsg={jumpToMsg}
            />
          ))}
        </React.Fragment>
      ))}
    </ul>
  );

  return (
    <Flex vertical className={modalClasses}>
      <Flex
        align="center"
        justify="space-between"
        gap="small"
        className="info__header"
      >
        <Flex align="center" gap="small">
          <span className="info__header--title">Search Results</span>
          <span className="info__header--subtitle">{displayName}</span>
        </Flex>

        <Button
          type="text"
          className="info__header--expand"
          onClick={() => onSectionUpdate({ isExpanded: !isExpanded })}
        >
          {isExpanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
        </Button>

        <Button
          type="text"
          className="info__header--close"
          onClick={() =>
            onSectionUpdate({ openedSection: "", isExpanded: false })
          }
        >
          <i className="fa-solid fa-xmark"></i>
        </Button>
      </Flex>

      <div className="info__subheader">
        <div className="MessagesOrFilesSelector">
          <div className="buttons-container">
            <button className="tab messages-tab active">
              <span>Messages</span>
              <span className="counter">{totalMessagesCount}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="search-result chat-body-wrapper">
        <div className="scroll-view">
          <div className="chat-wrapper">
            {isLoading
              ? renderLoadingState()
              : searchedMsgData.length === 0
              ? renderEmptyState()
              : renderSearchResults()}
          </div>
        </div>
      </div>
    </Flex>
  );
}
